/*
 * Henley Spirit search upgrades (mobile-first).
 * - Tap-to-search chips (popular searches + suggested queries)
 * - Predictive search asks only for products + query suggestions, 10 at a time
 *   (the section de-duplicates same-titled listings and shows up to 6)
 * - Shopify's search service sometimes fails or hangs. Instant results time out,
 *   retry once, then show a fallback instead of an endless spinner. The /search
 *   page reloads itself once when Shopify returns its "problem loading" error,
 *   then shows a friendly fallback.
 * Leaves predictive-search.js untouched; patches it once it has loaded.
 */
(function () {
	const RESOURCE_PARAMS = [
		'resources[type]=product,query',
		'resources[limit]=10',
		'resources[options][unavailable_products]=last',
		'resources[options][fields]=title,product_type,variants.title,tag,vendor',
	].join('&');
	const REQUEST_TIMEOUT_MS = 6000;
	const RETRY_DELAY_MS = 700;
	const SHOPIFY_ERROR_TEXT = 'problem loading your search results';

	function escapeHtml(text) {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}

	function categoryChipsHtml() {
		const chips = document.querySelector('.hs-search-start .hs-search-chips--links');
		return chips ? chips.outerHTML : '';
	}

	// One request with a timeout; also stops when the theme aborts it (form reset)
	function fetchSuggestions(searchTerm, outerSignal) {
		const controller = new AbortController();
		const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
		const onOuterAbort = () => controller.abort();
		outerSignal.addEventListener('abort', onOuterAbort);

		return fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${RESOURCE_PARAMS}&section_id=predictive-search`,
			{ signal: controller.signal }
		)
			.then((response) => {
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				return response.text();
			})
			.then((text) => {
				const section = new DOMParser()
					.parseFromString(text, 'text/html')
					.querySelector('#shopify-section-predictive-search');
				if (!section || !section.querySelector('#predictive-search-results')) {
					throw new Error('Unexpected response');
				}
				return section.innerHTML;
			})
			.finally(() => {
				clearTimeout(timer);
				outerSignal.removeEventListener('abort', onOuterAbort);
			});
	}

	function fallbackMarkup(searchTerm) {
		const term = escapeHtml(searchTerm);
		return `
			<div id="predictive-search-results" class="hs-search-results" role="listbox" tabIndex="-1">
				<div class="hs-search-block hs-search-empty">
					<p class="hs-search-empty__title">Instant results are taking a moment</p>
					<p class="hs-search-empty__text">Tap below to see everything for “${term}”, or browse:</p>
					${categoryChipsHtml()}
				</div>
				<div class="hs-search-all">
					<button type="submit" class="hs-search-all__btn button button--primary">See all results for “${term}”</button>
				</div>
			</div>
			<span class="hidden" data-predictive-search-live-region-count-value>Search for ${term}</span>`;
	}

	function patchPredictiveSearch() {
		if (typeof PredictiveSearch === 'undefined' || PredictiveSearch.prototype.hsPatched) return;
		const proto = PredictiveSearch.prototype;
		proto.hsPatched = true;

		proto.getSearchResults = function (searchTerm) {
			const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
			this.setLiveRegionLoadingState();

			if (this.cachedResults[queryKey]) {
				this.renderSearchResults(this.cachedResults[queryKey]);
				this.calcHeight();
				return;
			}

			if (this.promoModal && this.classList.contains('search-modal__form')) {
				this.promoModal.classList.add('search__modal--hidden');
			}

			const outerSignal = this.abortController.signal;
			const isCurrent = () => !outerSignal.aborted && this.getQuery() === searchTerm;

			fetchSuggestions(searchTerm, outerSignal)
				.catch((error) => {
					if (!isCurrent()) throw error;
					// Shopify search hiccup or timeout: try once more
					return new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS)).then(() => {
						if (!isCurrent()) throw error;
						return fetchSuggestions(searchTerm, outerSignal);
					});
				})
				.then((resultsMarkup) => {
					this.allPredictiveSearchInstances.forEach((instance) => {
						instance.cachedResults[queryKey] = resultsMarkup;
					});
					if (!isCurrent()) return;
					this.renderSearchResults(resultsMarkup);
					this.calcHeight();
				})
				.catch(() => {
					if (!isCurrent()) {
						this.removeAttribute('loading');
						return;
					}
					// Never leave a spinner: offer the full search page instead (not cached, so the next keystroke tries again)
					this.renderSearchResults(fallbackMarkup(searchTerm));
					this.calcHeight();
				});
		};
	}

	function runSearch(term) {
		const input =
			document.querySelector('#search-modal.active .search__input') ||
			document.querySelector('#Search-In-Template') ||
			document.querySelector('#search-modal .search__input');
		if (!input) return;
		input.value = term;
		input.dispatchEvent(new Event('input', { bubbles: true }));
		input.focus();
	}

	document.addEventListener('click', (event) => {
		const chip = event.target.closest('[data-hs-search-term]');
		if (!chip) return;
		event.preventDefault();
		runSearch(chip.dataset.hsSearchTerm);
	});

	// Bring popular searches back when the box is cleared (the theme only did this on keyup, not on the X button)
	function showStartPanel() {
		const panel = document.querySelector('#search-modal .hs-search-start');
		if (panel) panel.classList.remove('hidden');
	}
	document.addEventListener(
		'reset',
		(event) => {
			if (event.target.closest && event.target.closest('#search-modal')) setTimeout(showStartPanel);
		},
		true
	);
	document.addEventListener('input', (event) => {
		if (event.target.matches && event.target.matches('#search-modal .search__input') && !event.target.value.trim()) {
			showStartPanel();
		}
	});

	// /search page: Shopify prints its error text wherever the page asks for results when its search service fails
	function recoverSearchPage() {
		const page = document.querySelector('.template-search');
		if (!page) return;
		const retryKey = `hs-search-retry:${location.search}`;
		if (!page.textContent.toLowerCase().includes(SHOPIFY_ERROR_TEXT)) {
			try {
				sessionStorage.removeItem(retryKey);
			} catch (e) {}
			return;
		}

		let retries = 0;
		try {
			retries = parseInt(sessionStorage.getItem(retryKey) || '0', 10);
		} catch (e) {}

		if (retries < 2) {
			try {
				sessionStorage.setItem(retryKey, String(retries + 1));
			} catch (e) {}
			page.style.opacity = '0.3';
			setTimeout(() => location.reload(), 800 * (retries + 1));
			return;
		}

		// Still failing: hide Shopify's repeated error lines and show one clear message
		const walker = document.createTreeWalker(page, NodeFilter.SHOW_TEXT);
		const errorNodes = [];
		while (walker.nextNode()) {
			if (walker.currentNode.nodeValue.toLowerCase().includes(SHOPIFY_ERROR_TEXT)) errorNodes.push(walker.currentNode);
		}
		errorNodes.forEach((node) => node.remove());

		const term = new URLSearchParams(location.search).get('q') || '';
		const notice = document.createElement('div');
		notice.className = 'hs-search-page-error container';
		notice.innerHTML = `
			<p class="hs-search-empty__title">Search is having a moment</p>
			<p class="hs-search-empty__text">Please try again, or browse our collections below.</p>
			<a class="button button--primary hs-search-all__btn" href="${location.pathname}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last">Try “${escapeHtml(term)}” again</a>
			<div class="hs-search-page-error__links">${categoryChipsHtml()}</div>`;
		const header = page.querySelector('.template-search__header');
		(header || page).insertAdjacentElement('afterend', notice);
		try {
			sessionStorage.removeItem(retryKey);
		} catch (e) {}
	}

	// This file loads before predictive-search.js, so wait until that class exists before patching it
	patchPredictiveSearch();
	if (window.customElements) {
		customElements.whenDefined('predictive-search').then(patchPredictiveSearch);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', recoverSearchPage);
	} else {
		recoverSearchPage();
	}
})();
